import React from "react"
import { View, ViewStyle } from "react-native"

import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withSequence,
  withTiming,
} from "react-native-reanimated"
import { SidekickNameT } from "types/avatar"
import { EskTypeT, SponsorT } from "types/sponsor"

import { useAppTheme } from "src/hooks/useAppTheme"
import { useCurrentConversation } from "src/queries/useConversation"
import { ThemedStyle } from "src/theme"
import { MessageBubble } from "./MessageBubble"

const DOT_TIMING = 300

interface ThinkingIndicatorProps {
  isUser?: boolean
}

export const ThinkingIndicator = (props: ThinkingIndicatorProps) => {
  const { themed } = useAppTheme()

  const { data: currentConversation } = useCurrentConversation()
  const activeESKSponsor = currentConversation?.active_esk_sponsor as SponsorT
  const activeESKType = currentConversation?.active_esk_type as EskTypeT
  const activeSidekickName = currentConversation?.sidekick_name as SidekickNameT

  const dot1Opacity = useSharedValue(1)
  const dot2Opacity = useSharedValue(1)
  const dot3Opacity = useSharedValue(1)

  const dot1Style = useAnimatedStyle(() => ({
    opacity: dot1Opacity.value,
  }))
  const dot2Style = useAnimatedStyle(() => ({
    opacity: dot2Opacity.value,
  }))
  const dot3Style = useAnimatedStyle(() => ({
    opacity: dot3Opacity.value,
  }))

  React.useEffect(() => {
    const animateDots = () => {
      // dot1Opacity.value = withDecay({ velocity: 0.5 })
      dot1Opacity.value = withSequence(
        withTiming(0, { duration: DOT_TIMING, easing: Easing.linear }),
        withTiming(1, { duration: DOT_TIMING, easing: Easing.linear }),
      )
      dot2Opacity.value = withSequence(
        withTiming(0, { duration: DOT_TIMING * 2, easing: Easing.linear }),
        withTiming(1, { duration: DOT_TIMING * 2, easing: Easing.linear }),
      )
      dot3Opacity.value = withSequence(
        withTiming(0, { duration: DOT_TIMING * 3, easing: Easing.linear }),
        withTiming(1, { duration: DOT_TIMING * 3, easing: Easing.linear }),
      )
    }

    animateDots()
    const interval = setInterval(animateDots, DOT_TIMING * 6) // loop every full cycle

    return () => clearInterval(interval) // cleanup on unmount
  }, [dot1Opacity, dot2Opacity, dot3Opacity])

  return (
    <View>
      <MessageBubble
        item={{
          role: props.isUser ? "member" : "sidekick",
          active_esk_sponsor: activeESKSponsor,
          active_esk_type: activeESKType,
          sidekick_name: activeSidekickName,
        }}
        thinkingIndicator={true}
        style={$bubbleWidthOverride}
      >
        <View style={$dotsContainer}>
          <Animated.View style={[themed($dot), dot1Style]} />
          <Animated.View style={[themed($dot), dot2Style]} />
          <Animated.View style={[themed($dot), dot3Style]} />
        </View>
      </MessageBubble>
    </View>
  )
}

const $dot: ThemedStyle<ViewStyle> = ({ colors }) => ({
  backgroundColor: colors.thinkingDot,
  borderRadius: 2,
  height: 4,
  marginHorizontal: 2,
  width: 4,
})

const $dotsContainer: ViewStyle = {
  alignItems: "center",
  flexDirection: "row",
  justifyContent: "center",
  marginVertical: 10,
}

const $bubbleWidthOverride: ViewStyle = {
  width: 70,
}
